import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import { useAuth } from '../../contexts/AuthContext';
import { formatCpf } from '../../utils/cpf';

export default function MeuPerfil() {
  const { user, isAdmin, updateMe } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    nome: user?.nome || '',
    sobrenome: user?.sobrenome || '',
    data_nascimento: user?.data_nascimento || '',
    sexo: user?.sexo || '',
    email: user?.email || '',
    cpf: user?.cpf || '',
  });
  const [alterarSenha, setAlterarSenha] = useState(false);
  const [senha, setSenha] = useState({ nova: '', confirmar: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (f) => (e) => setForm(prev => ({ ...prev, [f]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (alterarSenha && senha.nova !== senha.confirmar) {
      setError('As senhas não coincidem');
      return;
    }
    if (alterarSenha && senha.nova.length < 6) {
      setError('A nova senha deve ter ao menos 6 caracteres');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nome: form.nome || null,
        sobrenome: form.sobrenome || null,
        data_nascimento: form.data_nascimento || null,
        sexo: form.sexo || null,
      };
      if (isAdmin) {
        payload.email = form.email;
        payload.cpf = form.cpf ? form.cpf.replace(/\D/g, '') : null;
      }
      if (alterarSenha && senha.nova) {
        payload.senha = senha.nova;
      }

      await updateMe(payload);
      setSenha({ nova: '', confirmar: '' });
      setAlterarSenha(false);
      setSuccess('Perfil atualizado com sucesso!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button className="btn btn-sm btn-link text-muted mb-3 ps-0" onClick={() => navigate(-1)}>
        <i className="bi bi-arrow-left me-1"></i>Voltar
      </button>

      <PageHeader title="Meu perfil" subtitle="Edite suas informações pessoais" />

      <div className="card-datafit p-4" style={{ maxWidth: 560 }}>
        {error && (
          <div className="alert alert-danger py-2 small mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>{error}
          </div>
        )}
        {success && (
          <div className="alert alert-success py-2 small mb-3">
            <i className="bi bi-check-circle me-2"></i>{success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="row g-3 mb-3">
            <div className="col-sm-6">
              <label className="form-label small fw-500">Nome</label>
              <input type="text" className="form-control" value={form.nome} onChange={set('nome')} />
            </div>
            <div className="col-sm-6">
              <label className="form-label small fw-500">Sobrenome</label>
              <input type="text" className="form-control" value={form.sobrenome} onChange={set('sobrenome')} />
            </div>
            <div className="col-sm-6">
              <label className="form-label small fw-500">Data de nascimento</label>
              <input type="date" className="form-control" value={form.data_nascimento} onChange={set('data_nascimento')} />
            </div>
            <div className="col-sm-6">
              <label className="form-label small fw-500">Sexo</label>
              <select className="form-select" value={form.sexo} onChange={set('sexo')}>
                <option value="">Selecione</option>
                <option value="M">Masculino</option>
                <option value="F">Feminino</option>
              </select>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-sm-6">
              <label className="form-label small fw-500">E-mail</label>
              <input
                type="email"
                className="form-control"
                value={form.email}
                onChange={set('email')}
                disabled={!isAdmin}
              />
              {!isAdmin && <div className="form-text small text-muted">Somente admin pode alterar.</div>}
            </div>
            <div className="col-sm-6">
              <label className="form-label small fw-500">CPF</label>
              <input
                type="text"
                className="form-control"
                placeholder="000.000.000-00"
                value={form.cpf}
                onChange={(e) => setForm(prev => ({ ...prev, cpf: formatCpf(e.target.value) }))}
                disabled={!isAdmin}
              />
              {!isAdmin && <div className="form-text small text-muted">Somente admin pode alterar.</div>}
            </div>
          </div>

          <hr className="my-3" />

          {!alterarSenha ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mb-4"
              onClick={() => setAlterarSenha(true)}
            >
              <i className="bi bi-key me-2"></i>Alterar senha
            </button>
          ) : (
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small fw-bold text-muted text-uppercase" style={{ fontSize: '.7rem', letterSpacing: '.06em' }}>
                  Nova senha
                </span>
                <button type="button" className="btn btn-link btn-sm text-muted p-0"
                  onClick={() => { setAlterarSenha(false); setSenha({ nova: '', confirmar: '' }); }}>
                  Cancelar
                </button>
              </div>
              <div className="row g-3">
                <div className="col-sm-6">
                  <label className="form-label small fw-500">Nova senha</label>
                  <input
                    type="password"
                    className="form-control"
                    value={senha.nova}
                    onChange={e => setSenha(s => ({ ...s, nova: e.target.value }))}
                    minLength={6}
                    autoFocus
                  />
                </div>
                <div className="col-sm-6">
                  <label className="form-label small fw-500">Confirmar nova senha</label>
                  <input
                    type="password"
                    className="form-control"
                    value={senha.confirmar}
                    onChange={e => setSenha(s => ({ ...s, confirmar: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="d-flex gap-2">
            <button type="submit" className="btn-datafit" disabled={saving}>
              {saving ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-check-lg me-2"></i>}
              Salvar
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
